/**
 * Razorpay Checkout helper.
 * Lazily injects the official Razorpay JS SDK and exposes a single
 * `openRazorpay(opts)` function. Returns a promise that resolves with
 * the success payload or rejects on dismiss/error.
 */
const RZP_SCRIPT_SRC = "https://checkout.razorpay.com/v1/checkout.js";

let scriptLoading = null;

function loadScript() {
  if (typeof window !== "undefined" && window.Razorpay) return Promise.resolve();
  if (scriptLoading) return scriptLoading;
  scriptLoading = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = RZP_SCRIPT_SRC;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => {
      scriptLoading = null;
      reject(new Error("Could not load Razorpay Checkout — please retry."));
    };
    document.body.appendChild(s);
  });
  return scriptLoading;
}

/**
 * Open Razorpay Standard Checkout.
 * @param {object} cfg - server-issued checkout config
 *   { key_id, order_id, amount, currency, name, description, prefill, theme? }
 * @returns {Promise<object>} resolves with razorpay_* fields on success
 */
export async function openRazorpay(cfg) {
  await loadScript();
  return new Promise((resolve, reject) => {
    if (!cfg.key_id) {
      reject(new Error("Online payments not configured — please use cash on delivery"));
      return;
    }
    const rzp = new window.Razorpay({
      key: cfg.key_id,
      order_id: cfg.order_id,
      amount: cfg.amount,
      currency: cfg.currency || "INR",
      name: cfg.name || "Rescigo",
      description: cfg.description || "",
      prefill: cfg.prefill || {},
      theme: { color: cfg.theme || "#2D5A27" },
      handler: (response) => resolve(response),
      modal: {
        ondismiss: () => reject(new Error("Payment cancelled")),
      },
    });
    rzp.on("payment.failed", (resp) => {
      reject(new Error(resp?.error?.description || "Payment failed"));
    });
    rzp.open();
  });
}
